import { action, computed, flow, makeObservable, observable } from "mobx";
import moment from "moment";
import { apiFetcher, downloadDocument } from "../utils/fetch";

/*
// --- DUMMY / LOCAL STORAGE HELPER (Commented out - backend implemented) ---
const STORAGE_PREFIX = "superkos_deposit_";

function getLocalData(tenancyOrTenantId) {
  if (typeof window === "undefined" || !tenancyOrTenantId) return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${tenancyOrTenantId}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function setLocalData(tenancyOrTenantId, data) {
  if (typeof window === "undefined" || !tenancyOrTenantId) return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${tenancyOrTenantId}`, JSON.stringify(data));
  } catch (e) {
    // Ignore storage errors
  }
}
*/

export default class SecurityDeposit {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.deposit = null;
    this.items = [];
    this.transactions = [];
    this.loading = false;
    this.error = null;

    makeObservable(this, {
      deposit: observable,
      items: observable,
      transactions: observable,
      loading: observable,
      error: observable,

      tenancy: computed,
      requiredAmount: computed,
      receivedAmount: computed,
      deductedAmount: computed,
      refundedAmount: computed,
      outstandingAmount: computed,
      heldAmount: computed,
      status: computed,
      hasDeposit: computed,
      isConcludedTenancy: computed,
      isCurrentTenancy: computed,

      setTenancy: action,
      setDeposit: action,
      setItems: action,
      setTransactions: action,
      setLoading: action,
      setError: action,

      fetch: flow,
      fetchAll: flow,
      fetchByTenancyId: flow,
      fetchTransactions: flow,
      createDeposit: flow,
      receiveDeposit: flow,
      adjustDeposit: flow,
      deductDeposit: flow,
      refundDeposit: flow,
      cancelTransaction: flow,
      downloadDeposit: flow,
      sendDeposit: flow,
    });
  }

  get tenancy() {
    return this.rootStore?.tenancy?.selected || null;
  }

  setTenancy = (tenancy) => {
    this.rootStore?.tenancy?.setSelected(tenancy);
  };
  setDeposit = (deposit) => (this.deposit = deposit);
  setItems = (items) => (this.items = items);
  setTransactions = (txs) => (this.transactions = txs);
  setLoading = (loading) => (this.loading = loading);
  setError = (error) => (this.error = error);

  get requiredAmount() {
    return Number(this.deposit?.requiredAmount || 0);
  }

  get receivedAmount() {
    return Number(this.deposit?.receivedAmount || 0);
  }

  get deductedAmount() {
    return Number(this.deposit?.deductedAmount || 0);
  }

  get refundedAmount() {
    return Number(this.deposit?.refundedAmount || 0);
  }

  get outstandingAmount() {
    return Math.max(0, this.requiredAmount - this.receivedAmount);
  }

  get heldAmount() {
    return Math.max(0, this.receivedAmount - this.deductedAmount - this.refundedAmount);
  }

  get status() {
    return this.deposit?.status || (this.deposit ? "PENDING" : null);
  }

  get hasDeposit() {
    return !!this.deposit && this.requiredAmount > 0;
  }

  get isConcludedTenancy() {
    return this.rootStore?.tenancy?.isConcludedTenancy ?? false;
  }

  get isCurrentTenancy() {
    return this.rootStore?.tenancy?.isCurrentTenancy ?? false;
  }

  // Calculate lifecycle status according to business logic (client fallback helper)
  _calculateStatus(deposit) {
    const required = Number(deposit.requiredAmount || 0);
    const received = Number(deposit.receivedAmount || 0);
    const deducted = Number(deposit.deductedAmount || 0);
    const refunded = Number(deposit.refundedAmount || 0);
    const held = Math.max(0, received - deducted - refunded);

    if (deposit.status === "CANCELLED") {
      return "CANCELLED";
    }

    if (received === 0) {
      return "PENDING";
    }

    if (received < required && deducted === 0 && refunded === 0) {
      return "PARTIALLY_PAID";
    }

    if (refunded > 0) {
      return held === 0 ? "REFUNDED" : "PARTIALLY_REFUNDED";
    }

    if (deducted > 0) {
      return held === 0 ? "REFUNDED" : "PARTIALLY_DEDUCTED";
    }

    if (received >= required) {
      return "HELD";
    }

    return "PENDING";
  }

  *fetchAll(params = {}) {
    this.setLoading(true);
    this.setError(null);
    try {
      const res = yield apiFetcher().get("/security-deposits", { params });
      let list = [];
      if (Array.isArray(res.data)) {
        list = res.data;
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
      }
      this.setItems(list);
      this.setLoading(false);
      return { status: 200, data: list };
    } catch (error) {
      console.error("[SecurityDeposit] fetchAll error:", error);
      this.setError("Unable to load security deposits");
      this.setLoading(false);
      return { status: error?.response?.status || 500, error };
    }
  }

  *fetch(tenantId) {
    this.setLoading(true);
    this.setError(null);

    const tenant = this.rootStore?.tenant?.selected || {};
    const tId = tenantId || tenant._id;

    try {
      // 1. Ensure tenancy is loaded in rootStore.tenancy
      let tenancy = this.rootStore?.tenancy?.selected;
      if (!tenancy || (tId && tenancy.tenantId !== tId)) {
        yield this.rootStore?.tenancy?.fetchByTenantId(tId);
        tenancy = this.rootStore?.tenancy?.selected;
      }

      if (!tenancy?._id) {
        this.setDeposit(null);
        this.setTransactions([]);
        this.setLoading(false);
        return { status: 200, data: null };
      }

      // 2. Fetch security deposit using the direct tenancyId endpoint (/security-deposits/tenancy/:tenancyId)
      return yield this.fetchByTenancyId(tenancy._id);

      /*
      // --- DUMMY HANDLER (Commented out - backend implemented) ---
      const contextId = tenancy._id || tId;
      let existingData = getLocalData(contextId);

      if (existingData && existingData.deposit) {
        this.setDeposit(existingData.deposit);
        this.setTransactions(existingData.transactions || []);
      } else {
        const legacyAmount = Number(tenant.securityDeposit?.amount || 0);
        const legacyPaid = tenant.securityDeposit?.isPaidFront || false;

        if (legacyAmount > 0) {
          const initialReceived = legacyPaid ? legacyAmount : 0;
          const initialDeposit = {
            _id: `dep_${contextId}`,
            tenancyId: tenancy._id,
            tenantId: tId,
            requiredAmount: legacyAmount,
            receivedAmount: initialReceived,
            deductedAmount: 0,
            refundedAmount: 0,
            currency: "IDR",
            status: initialReceived >= legacyAmount ? "HELD" : "PENDING",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          const initialTxs = [];
          if (initialReceived > 0) {
            initialTxs.push({
              _id: `tx_init_${contextId}`,
              depositId: initialDeposit._id,
              tenancyId: tenancy._id,
              type: "RECEIVED",
              amount: initialReceived,
              date: new Date().toISOString(),
              paymentMethod: "transfer",
              description: "Initial deposit payment",
              createdAt: new Date().toISOString(),
            });
          }

          this.setDeposit(initialDeposit);
          this.setTransactions(initialTxs);
        } else {
          const dummyDeposit = {
            _id: `dep_${contextId}`,
            tenancyId: tenancy._id,
            tenantId: tId,
            requiredAmount: 0,
            receivedAmount: 0,
            deductedAmount: 0,
            refundedAmount: 0,
            currency: "IDR",
            status: "HELD",
            createdAt: moment().subtract(15, "days").toISOString(),
            updatedAt: moment().subtract(15, "days").toISOString(),
          };
          this.setDeposit(dummyDeposit);
        }
      }
      */
    } catch (error) {
      console.error("[SecurityDeposit] Fetch error:", error);
      this.setError("Unable to load security deposit");
      this.setLoading(false);
      return { status: 500, error };
    }
  }

  *fetchByTenancyId(tenancyId) {
    if (!tenancyId) {
      this.setDeposit(null);
      this.setTransactions([]);
      return { status: 400, error: "Tenancy ID is required" };
    }

    this.setLoading(true);
    this.setError(null);

    try {
      let depositRes;
      try {
        // Direct endpoint: GET /security-deposits/tenancy/:tenancyId
        depositRes = yield apiFetcher().get(`/security-deposits/tenancy/${tenancyId}`);
      } catch (err) {
        if (err?.response?.status === 404) {
          this.setDeposit(null);
          this.setTransactions([]);
          this.setLoading(false);
          return { status: 200, data: null };
        }
        // Fallback to query param endpoint if path route not matched
        depositRes = yield apiFetcher().get("/security-deposits", {
          params: { tenancyId },
        });
      }

      let depositData = null;
      if (Array.isArray(depositRes.data)) {
        depositData = depositRes.data.length > 0 ? depositRes.data[0] : null;
      } else if (depositRes.data && depositRes.data._id) {
        depositData = depositRes.data;
      } else if (depositRes.data?.data) {
        depositData = Array.isArray(depositRes.data.data) ? depositRes.data.data[0] : depositRes.data.data;
      }

      if (depositData && depositData._id) {
        this.setDeposit(depositData);
        if (Array.isArray(depositData.transactions) && depositData.transactions.length > 0) {
          this.setTransactions(depositData.transactions);
        } else {
          yield this.fetchTransactions(depositData._id);
        }
        this.setLoading(false);
        return { status: 200, data: depositData };
      } else {
        this.setDeposit(null);
        this.setTransactions([]);
        this.setLoading(false);
        return { status: 200, data: null };
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        this.setDeposit(null);
        this.setTransactions([]);
        this.setLoading(false);
        return { status: 200, data: null };
      }
      console.error("[SecurityDeposit] fetchByTenancyId error from backend:", err);
      this.setError(err?.response?.data?.message || "Unable to load security deposit");
      this.setLoading(false);
      return { status: err?.response?.status || 500, error: err };
    }
  }

  *fetchTransactions(depositId) {
    const id = depositId || this.deposit?._id;
    if (!id) return;
    try {
      const res = yield apiFetcher().get(`/security-deposits/${id}/transactions`);
      if (Array.isArray(res.data)) {
        this.setTransactions(res.data);
      } else if (Array.isArray(res.data?.data)) {
        this.setTransactions(res.data.data);
      }
    } catch (e) {
      console.error("[SecurityDeposit] fetchTransactions error:", e);
    }
  }

  *createDeposit({ requiredAmount, description }) {
    if (this.isConcludedTenancy) {
      console.warn("[SecurityDeposit] Cannot create deposit for a concluded tenancy.");
      return { status: 400, error: "Cannot create deposit for a concluded tenancy." };
    }
    this.setLoading(true);

    try {
      const payload = {
        tenancyId: this.tenancy?._id,
        tenantId: this.tenancy?.tenantId || this.rootStore?.tenant?.selected?._id,
        requiredAmount: Number(requiredAmount),
        currency: "IDR",
        description: description || null,
      };

      const res = yield apiFetcher().post("/security-deposits", payload);
      const newDeposit = res.data;

      this.setDeposit(newDeposit);
      this.setTransactions(newDeposit.transactions || []);
      this.setLoading(false);
      return { status: 200, data: newDeposit };

      /*
      // --- DUMMY FALLBACK (Commented out - backend implemented) ---
      let dummyDeposit = {
        _id: `dep_${Date.now()}`,
        tenancyId: payload.tenancyId,
        tenantId: payload.tenantId,
        requiredAmount: payload.requiredAmount,
        receivedAmount: 0,
        deductedAmount: 0,
        refundedAmount: 0,
        currency: "IDR",
        status: "PENDING",
        description: payload.description,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      this.setDeposit(dummyDeposit);
      this.setLoading(false);
      return { status: 200, data: dummyDeposit };
      */
    } catch (error) {
      console.error("[SecurityDeposit] Create error:", error);
      this.setLoading(false);
      return { status: error?.response?.status || 500, error };
    }
  }

  *receiveDeposit({ amount, paymentMethod, date, reference, note }) {
    if (!this.deposit) return { status: 400 };
    if (this.isConcludedTenancy) {
      console.warn("[SecurityDeposit] Cannot receive deposit for a concluded tenancy.");
      return { status: 400, error: "Cannot receive deposit for a concluded tenancy." };
    }
    const numAmount = Number(amount);
    if (numAmount <= 0 || numAmount > this.outstandingAmount) {
      return { status: 400, error: "Invalid payment amount." };
    }

    try {
      const methodMap = {
        transfer: "bank_transfer",
        cash: "cash",
        credit_card: "credit_card",
        e_wallet: "e_wallet",
        midtrans: "midtrans",
        xendit: "xendit",
      };
      const method = methodMap[paymentMethod] || paymentMethod || "bank_transfer";

      const payload = {
        amount: numAmount,
        method,
        notes: note,
        paidAt: date ? moment(date).toISOString() : new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        reference,
      };

      const res = yield apiFetcher().post(`/security-deposits/${this.deposit._id}/receive`, payload);
      const updatedDeposit = res.data;

      if (updatedDeposit) {
        this.setDeposit(updatedDeposit);
      }
      yield this.fetchTransactions(this.deposit._id);

      return { status: 200, data: updatedDeposit || this.deposit };

      /*
      // --- DUMMY FALLBACK (Commented out - backend implemented) ---
      const mockDeposit = {
        ...this.deposit,
        receivedAmount: this.receivedAmount + numAmount,
        updatedAt: new Date().toISOString(),
      };
      mockDeposit.status = this._calculateStatus(mockDeposit);

      const newTx = {
        _id: `tx_${Date.now()}`,
        depositId: mockDeposit._id,
        tenancyId: this.tenancy?._id,
        type: "RECEIVED",
        amount: numAmount,
        date: payload.paidAt,
        paymentMethod: method,
        reference,
        description: note || `Deposit received via ${paymentMethod}`,
        createdAt: new Date().toISOString(),
      };

      this.setDeposit(mockDeposit);
      this.setTransactions([newTx, ...this.transactions]);
      return { status: 200, data: mockDeposit };
      */
    } catch (error) {
      console.error("[SecurityDeposit] Receive error:", error);
      return { status: error?.response?.status || 500, error };
    }
  }

  *adjustDeposit({ newRequiredAmount, note }) {
    if (!this.deposit) return { status: 400 };
    if (this.isConcludedTenancy) {
      console.warn("[SecurityDeposit] Cannot adjust deposit for a concluded tenancy.");
      return { status: 400, error: "Cannot adjust deposit for a concluded tenancy." };
    }
    const numNew = Number(newRequiredAmount);

    try {
      const payload = {
        newRequiredAmount: numNew,
        notes: note,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const res = yield apiFetcher().post(`/security-deposits/${this.deposit._id}/adjust`, payload);
      const updatedDeposit = res.data;

      if (updatedDeposit) {
        this.setDeposit(updatedDeposit);
      }
      yield this.fetchTransactions(this.deposit._id);

      return { status: 200, data: updatedDeposit || this.deposit };

      /*
      // --- DUMMY FALLBACK (Commented out - backend implemented) ---
      const diff = numNew - this.requiredAmount;
      const mockDeposit = {
        ...this.deposit,
        requiredAmount: numNew,
        updatedAt: new Date().toISOString(),
      };
      mockDeposit.status = this._calculateStatus(mockDeposit);

      const newTx = {
        _id: `tx_${Date.now()}`,
        depositId: mockDeposit._id,
        tenancyId: this.tenancy?._id,
        type: "ADJUSTMENT",
        amount: diff,
        date: new Date().toISOString(),
        reason,
        description: reason || `Adjusted deposit requirement to Rp ${numNew.toLocaleString("id-ID")}`,
        createdAt: new Date().toISOString(),
      };

      this.setDeposit(mockDeposit);
      this.setTransactions([newTx, ...this.transactions]);
      return { status: 200, data: mockDeposit };
      */
    } catch (error) {
      console.error("[SecurityDeposit] Adjust error:", error);
      return { status: error?.response?.status || 500, error };
    }
  }

  *deductDeposit({ amount, reason, note }) {
    if (!this.deposit) return { status: 400 };
    const numAmount = Number(amount);
    if (numAmount <= 0 || numAmount > this.heldAmount) {
      return { status: 400, error: "Deduction amount exceeds held amount." };
    }

    try {
      const payload = {
        amount: numAmount,
        reason,
        notes: note,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const res = yield apiFetcher().post(`/security-deposits/${this.deposit._id}/deduct`, payload);
      const updatedDeposit = res.data;

      if (updatedDeposit) {
        this.setDeposit(updatedDeposit);
      }
      yield this.fetchTransactions(this.deposit._id);

      return { status: 200, data: updatedDeposit || this.deposit };

      /*
      // --- DUMMY FALLBACK (Commented out - backend implemented) ---
      const mockDeposit = {
        ...this.deposit,
        deductedAmount: this.deductedAmount + numAmount,
        updatedAt: new Date().toISOString(),
      };
      mockDeposit.status = this._calculateStatus(mockDeposit);

      const newTx = {
        _id: `tx_${Date.now()}`,
        depositId: mockDeposit._id,
        tenancyId: this.tenancy?._id,
        type: "DEDUCTION",
        amount: numAmount,
        date: new Date().toISOString(),
        reason,
        description: description || reason,
        createdAt: new Date().toISOString(),
      };

      this.setDeposit(mockDeposit);
      this.setTransactions([newTx, ...this.transactions]);
      return { status: 200, data: mockDeposit };
      */
    } catch (error) {
      console.error("[SecurityDeposit] Deduct error:", error);
      return { status: error?.response?.status || 500, error };
    }
  }

  *refundDeposit({ amount, paymentMethod, date, reference, note }) {
    if (!this.deposit) return { status: 400 };
    const numAmount = Number(amount);
    if (numAmount <= 0 || numAmount > this.heldAmount) {
      return { status: 400, error: "Refund amount exceeds held amount." };
    }

    try {
      const methodMap = {
        transfer: "bank_transfer",
        cash: "cash",
        credit_card: "credit_card",
        e_wallet: "e_wallet",
        midtrans: "midtrans",
        xendit: "xendit",
      };
      const method = methodMap[paymentMethod] || paymentMethod || "bank_transfer";

      const payload = {
        amount: numAmount,
        method,
        occuredAt: date ? moment(date).toISOString() : new Date().toISOString(),
        notes: note, // || (reference ? `Ref: ${reference}` : null),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const res = yield apiFetcher().post(`/security-deposits/${this.deposit._id}/refund`, payload);
      const updatedDeposit = res.data;

      if (updatedDeposit) {
        this.setDeposit(updatedDeposit);
      }
      yield this.fetchTransactions(this.deposit._id);

      return { status: 200, data: updatedDeposit || this.deposit };

      /*
      // --- DUMMY FALLBACK (Commented out - backend implemented) ---
      const mockDeposit = {
        ...this.deposit,
        refundedAmount: this.refundedAmount + numAmount,
        updatedAt: new Date().toISOString(),
      };
      mockDeposit.status = this._calculateStatus(mockDeposit);

      const newTx = {
        _id: `tx_${Date.now()}`,
        depositId: mockDeposit._id,
        tenancyId: this.tenancy?._id,
        type: "REFUND",
        amount: numAmount,
        date: payload.paidAt,
        paymentMethod: method,
        reference,
        description: note || `Refund via ${paymentMethod}`,
        createdAt: new Date().toISOString(),
      };

      this.setDeposit(mockDeposit);
      this.setTransactions([newTx, ...this.transactions]);
      return { status: 200, data: mockDeposit };
      */
    } catch (error) {
      console.error("[SecurityDeposit] Refund error:", error);
      return { status: error?.response?.status || 500, error };
    }
  }

  *cancelTransaction({ transactionId, reason } = {}) {
    if (!this.deposit) return { status: 400, error: "Deposit not found" };
    if (!transactionId) return { status: 400, error: "Transaction ID is required" };

    try {
      const payload = {
        reason: reason?.trim() || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const res = yield apiFetcher().post(
        `/security-deposits/${this.deposit._id}/transactions/${transactionId}/cancel`,
        payload,
      );

      const data = res.data;
      if (data?.deposit) {
        this.setDeposit(data.deposit);
      }
      yield this.fetchTransactions(this.deposit._id);

      return { status: 200, data };
    } catch (error) {
      console.error("[SecurityDeposit] Cancel transaction error:", error);
      const errorMessage =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to cancel deposit transaction";
      return { status: error?.response?.status || 500, error: errorMessage };
    }
  }

  *downloadDeposit(options = {}) {
    const targetDeposit = options?.deposit || this.deposit;
    const depositId = options?.depositId || targetDeposit?._id;
    if (!depositId) {
      return { status: 400, error: "No security deposit found" };
    }
    const termValue = options?.term || moment().format("YYYYMMDDHHmmss");
    const tenant = options?.tenant || this.rootStore?.tenant?.selected || {};
    const ref = `SD-${depositId.toString().slice(-6).toUpperCase()}`;
    const cleanTenantName = (tenant.name || "Tenant").replace(/[\\/:*?"<>|]/g, "");
    const documentName = `Deposit-${cleanTenantName}-${ref}.pdf`;

    try {
      const res = yield downloadDocument({
        endpoint: `/documents/deposit_receipt/${depositId}/${termValue}`,
        documentName,
      });
      return res;
    } catch (error) {
      console.error("[SecurityDeposit] Download error:", error);
      return { status: error?.response?.status || 500, error };
    }
  }

  *sendDeposit(options = {}) {
    const targetDeposit = options?.deposit || this.deposit;
    const depositId = options?.depositId || targetDeposit?._id;
    if (!depositId) {
      return { status: 400, error: "No security deposit found" };
    }
    const tenant = options?.tenant || this.rootStore?.tenant?.selected || {};
    const tenancy = options?.tenancy || this.rootStore?.tenancy?.selected || this.tenancy || {};
    const propertyId = options?.propertyId || tenancy?.propertyId || tenant?.room?.propertyId;
    const roomId = options?.roomId || tenancy?.roomId || tenant?.room?._id;

    try {
      const payload = {
        recipient_id: tenant?._id,
        channel: options?.channel || "whatsapp",
        type: "deposit",
        payload: {
          realm: {
            identifier: targetDeposit?.realmId || this.rootStore?.organization?.selected?._id,
          },
          property: {
            identifier: propertyId,
          },
          room: {
            identifier: roomId,
          },
          tenant: {
            identifier: tenant?._id,
          },
          deposit: {
            identifier: depositId,
          },
        },
      };

      let response;
      try {
        response = yield apiFetcher().post(`/messages/deposit/${depositId}`, payload);
      } catch (err) {
        if (err?.response?.status === 404 && this.rootStore?.notification?.send) {
          response = yield this.rootStore.notification.send(payload);
        } else {
          throw err;
        }
      }

      return { status: 200, data: response?.data };
    } catch (error) {
      console.error("[SecurityDeposit] Send error:", error);
      const errorMessage =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send security deposit";
      return { status: error?.response?.status || 500, error: errorMessage };
    }
  }
}
